import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { SERVICES } from "../lib/site";
import SectionLabel from "../components/SectionLabel";

export default function Services() {
  return (
    <div data-testid="page-services">
      {/* PAGE HEADER */}
      <section className="bg-navy text-white">
        <div className="container-pro pt-24 pb-20 md:pt-32 md:pb-28">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slateblue-400">
            04 / SERVICES
          </span>
          <h1 className="mt-6 font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl max-w-4xl leading-[1.05]">
            Specialty electrical engineering services for transit
            infrastructure.
          </h1>
          <p className="mt-8 text-base md:text-lg text-white/75 max-w-2xl leading-relaxed">
            Three core capabilities — delivered as standalone scopes or
            integrated into a full design / commissioning package.
          </p>
        </div>
      </section>

      {/* SERVICES LIST */}
      <section className="bg-white">
        {SERVICES.map((s, i) => {
          const reverse = i % 2 === 1;
          return (
            <article
              key={s.id}
              id={s.id}
              data-testid={`service-detail-${s.id}`}
              className={`border-b border-hairline ${
                i % 2 === 1 ? "bg-surface-alt" : "bg-white"
              }`}
            >
              <div className="container-pro py-20 md:py-28">
                <div
                  className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center ${
                    reverse ? "lg:flex-row-reverse" : ""
                  }`}
                >
                  <div
                    className={`lg:col-span-6 ${
                      reverse ? "lg:order-2" : ""
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <span className="overline">{s.code}</span>
                      <span className="h-px w-12 bg-hairline-dark" />
                      <span className="overline">
                        Capability {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h2 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl text-navy tracking-tight leading-tight">
                      {s.title}
                    </h2>
                    <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-xl">
                      {s.short}
                    </p>

                    <ul className="mt-10 space-y-4 max-w-xl">
                      {s.points.map((p) => (
                        <li
                          key={p}
                          className="flex items-start gap-4 pb-4 border-b border-hairline"
                        >
                          <span className="inline-flex h-6 w-6 items-center justify-center bg-navy text-white shrink-0 mt-0.5">
                            <Check className="h-3.5 w-3.5" strokeWidth={3} />
                          </span>
                          <span className="text-sm md:text-base text-slate-700 leading-relaxed">
                            {p}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <Link
                      to="/contact"
                      data-testid={`service-cta-${s.id}`}
                      className="mt-10 inline-flex link-underline"
                    >
                      Request {s.title.split(" — ")[0]} scope
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>

                  <div
                    className={`lg:col-span-6 ${reverse ? "lg:order-1" : ""}`}
                  >
                    <div className="relative aspect-[4/3] overflow-hidden border border-hairline-dark bg-navy">
                      <img
                        src={s.image}
                        alt={s.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-tr from-navy/40 via-transparent to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-5 bg-navy/85 text-white">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slateblue-400">
                            {s.code} · Discipline
                          </span>
                          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slateblue-400">
                            MTA · NYCT
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </section>

      {/* PROCESS */}
      <section className="bg-navy text-white">
        <div className="container-pro section-pad">
          <SectionLabel
            code="05 / PROCESS"
            kicker="How we engage"
            title="A simple, contract-ready workflow."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-4 grid-overlap !border-white/15 [&>*]:!border-white/15">
            {[
              {
                k: "STEP 01",
                t: "Scope Intake",
                d: "Drawings, specs, RFP language reviewed within 48 hours.",
              },
              {
                k: "STEP 02",
                t: "Engineering Plan",
                d: "Deliverables, revisions, and submittal cadence documented.",
              },
              {
                k: "STEP 03",
                t: "Production",
                d: "Drafting / as-built / FAT-SAT execution with weekly status.",
              },
              {
                k: "STEP 04",
                t: "Closeout",
                d: "Final binder, archive package, and as-built turnover.",
              },
            ].map((p) => (
              <div key={p.k} className="bg-navy p-7 md:p-8">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slateblue-400">
                  {p.k}
                </div>
                <h4 className="mt-6 font-heading font-bold text-white text-lg">
                  {p.t}
                </h4>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">
                  {p.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
